import { type Address } from '@/shared/types/address';
import { type MaritalStatusCode } from '@/lib/maritalstatus';
import type { ISODateString, ISOTimestampString } from '@/shared/types/common';
import type { PersonId } from '@/shared/types/profile';

export interface Person {
  id: PersonId;
  data: Partial<{
    archived: boolean;
    firstname: string;
    lastname: string;
    preferredname: string;
    fullname: string;
    suffix: string;
    email: string;
    phone: string;
    birthdate: string;
    gender: Gender;
    address: Address;
    picture: string;
    household_id: string;
    campus_id: string;
    blocked: boolean;
    marital_status: MaritalStatusCode;
    spouse_id: string;
    member_status: string;
    uid: string;
    uids?: string[];
    emails?: string[];
    created: string;
    updated: string;
    source: string;
    pco_id: string;
    mhx_id: string;
    groups: {
      laygroup1?: string;
      laygroup2?: string;
    };
    /** PCO Planning Center group ids (for access / policy). */
    pco_group_ids?: string[];
    /** PCO Planning Center Services team ids (for access / policy). */
    pco_service_team_ids?: string[];
    tags?: Record<
      string,
      {
        created?: ISODateString;
        count?: number;
      }
    >;
  }>;
}

export type Gender = 'm' | 'f';

export function isPersonId(value: string, strict = true): value is PersonId {
  if (value?.length !== 20) {
    return false;
  }

  if (strict) {
    for (let i = 0; i < value.length; i++) {
      if (
        !(
          (value[i] >= '0' && value[i] <= '9') ||
          (value[i] >= 'a' && value[i] <= 'z') ||
          (value[i] >= 'A' && value[i] <= 'Z')
        )
      ) {
        return false;
      }
    }
  }

  return true;
}

export interface Household {
  id: string;
  data: {
    /**
     * Head of household
     */
    head: PersonId;
    /**
     * Others allowed to manage the household.
     */
    managers?: PersonId[];
    /**
     * Household members
     */
    members: PersonId[];
    /**
     * Household name
     */
    name: string;
    /**
     * Relationships
     */
    relationships?: Relationship[];
    /**
     * Last updated (ISO timestamp string)
     */
    updated?: ISODateString;
  };
}

export type RelationshipType = 'spouse' | 'child' | 'sibling' | 'parent';

export interface Relationship {
  type: RelationshipType;
  from: PersonId;
  to: PersonId;
  anniversary?: ISODateString;
}

export interface State {
  data: Person[];
  count: number;
  loaded: boolean;
}

export interface LoadPeopleResponse {
  items: Person[];
  count?: number;
}

export type LoadPersonResponse =
  | {
      exists: false;
    }
  | ({
      exists: true;
    } & Person);

export type UpdatePersonResponse = Person & { updated: true };

export interface Workspace {
  id: string;
  data: {
    title: string;
    description: string;
    created_at: string;
    updated_at: string;
    created_by: string;
    updated_by: string;
  };
}

export interface Milestone {
  profile_id: PersonId;
  occurred_at: ISODateString;
  milestone_id: string;
  created_by: string;
  created_at: ISOTimestampString;
  deleted_at?: ISOTimestampString;
}
